import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { atom, useAtomValue, useSetAtom } from 'jotai';

import { useAccountsService } from '@restworld/data-services';
import { EmployerUserContact } from '@restworld/utility-types';

import { useAuth } from './use-auth';

const isProd = process.env['NEXT_PUBLIC_APP_ENV'] === 'production';
export const employerUserContactAtom = atom<Partial<EmployerUserContact>>({});
employerUserContactAtom.debugLabel = 'employerUserContactAtom';
export const employerUserContactQueryKey = ['employer-user-contact'];

export const useEmployerUserContactQuery = () => {
  const setEmployerUserContact = useSetAtom(employerUserContactAtom);
  const accountServices = useAccountsService();
  const { user } = useAuth();
  const enableApiCall = Boolean(user && (user.type === 'employer' || user.type === 'employer_contact'));
  const router = useRouter();

  // Define a callback function to handle switching to the old version of the application
  const switchToOldVersion = useCallback(
    (switchToNewVersion?: boolean) => {
      // Get the current path from the router
      const currentPath = router.asPath;

      // Determine if the user should switch to the old version
      const isSwitchToOldVersion = switchToNewVersion === false;

      // Check if the current path is within the v2 employer section
      const isCurrentPathEmployer = currentPath.includes('/v2/employer');

      // and the current path is within the v2 employer section and isSwitchToOldVersion is true, redirect to the old employer job positions page
      if (isProd && isSwitchToOldVersion && isCurrentPathEmployer) {
        router.push('/employer/job-positions');
      }
    },
    [router]
  );

  const fetchEmployerUserContact = useCallback(async () => {
    const data = (await accountServices.fetchEmployerUserContact(user?.id)).data;
    setEmployerUserContact(data);
    switchToOldVersion(data.switch_to_new_version);

    return data;
  }, [user?.id, accountServices, setEmployerUserContact, switchToOldVersion]);

  const queryData = useQuery({
    queryKey: employerUserContactQueryKey,
    queryFn: fetchEmployerUserContact,
    enabled: enableApiCall,
  });

  return queryData;
};
export const useEmployerUserContactData = () => useAtomValue(employerUserContactAtom);

export const useEmployerUserInformationLoadingState = () =>
  useQueryClient().getQueryState(employerUserContactQueryKey)?.status === 'pending';
